import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShoppingCart, Crown, Sparkles, Package } from "lucide-react";
import { ICONS } from "@/components/marketplace/marketplaceIcons";

function getStatusBadge(module) {
  if (module.is_trial) {
    return <Badge className="bg-blue-100 text-blue-700 border-blue-200">Trial Ativo</Badge>;
  }
  if (module.is_included_in_plan) {
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200">Incluído no Plano</Badge>;
  }
  if (module.is_subscribed) {
    return <Badge className="bg-purple-100 text-purple-700 border-purple-200">Assinado</Badge>;
  }
  return <Badge variant="outline" className="text-gray-500">Não Ativo</Badge>;
}

export function PremiumModules({ modules, loading, status, trialDaysLeft, onSelectModule }) {
  if (modules.length === 0) return null;

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold flex items-center gap-2">
        <ShoppingCart className="w-5 h-5 text-emerald-600" />
        Módulos Premium
      </h2>

      {status === "trial" && trialDaysLeft > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-xl shrink-0">
              <Crown className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-blue-800">Período de Avaliação Gratuita</h3>
              <p className="text-sm text-blue-600 mt-1">
                Durante o trial, você tem acesso a <strong>todos os módulos</strong> sem custo adicional.
                Após o término, módulos não incluídos no seu plano serão bloqueados.
              </p>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map(module => {
            const Icon = ICONS[module.icon] || Package;
            return (
              <Card
                key={module.id}
                className={`relative overflow-hidden transition-all hover:shadow-lg cursor-pointer ${
                  module.is_active
                    ? "border-emerald-200 bg-gradient-to-br from-white to-emerald-50/30"
                    : "border-gray-200"
                }`}
                onClick={() => onSelectModule(module)}
                data-testid={`module-card-${module.id}`}
              >
                {module.is_active && (
                  <div className="absolute top-0 right-0 w-20 h-20 overflow-hidden">
                    <div className="absolute top-4 right-[-30px] w-[100px] text-center text-[10px] font-bold text-white bg-emerald-500 transform rotate-45 py-1">
                      ATIVO
                    </div>
                  </div>
                )}

                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2.5 rounded-xl ${module.is_active ? "bg-emerald-100" : "bg-gray-100"}`}>
                        <Icon className={`w-5 h-5 ${module.is_active ? "text-emerald-600" : "text-gray-500"}`} />
                      </div>
                      <div>
                        <CardTitle className="text-base">{module.name}</CardTitle>
                        <div className="mt-1">{getStatusBadge(module)}</div>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  <CardDescription className="text-sm mb-4">{module.description}</CardDescription>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-2xl font-bold text-gray-900">€{module.price.toFixed(2)}</span>
                      <span className="text-sm text-gray-500">/mês</span>
                    </div>
                    {!module.is_included_in_plan && !module.is_trial && (
                      <Button
                        size="sm"
                        variant={module.is_subscribed ? "outline" : "default"}
                        className={!module.is_subscribed ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                        onClick={(e) => { e.stopPropagation(); onSelectModule(module); }}
                      >
                        {module.is_subscribed ? "Gerenciar" : "Assinar"}
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
